/**
 * Safe-area inset provider.
 *
 * Reports how many CSS px along each edge are obscured by the device notch /
 * status bar / home indicator and by Telegram's own fullscreen chrome (the
 * close & menu buttons). The game lays its UI out inside this safe region.
 *
 * Sources, merged by taking the max per side:
 *  - Telegram WebApp `safeAreaInset` (device) + `contentSafeAreaInset` (TG UI),
 *    Bot API 8.0+, kept fresh via the safeAreaChanged / contentSafeAreaChanged
 *    / fullscreenChanged / viewportChanged events.
 *  - CSS `env(safe-area-inset-*)` (needs `viewport-fit=cover`) as a fallback
 *    for plain browsers and older clients.
 */

export interface Insets {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

type Listener = (insets: Insets) => void;

const listeners = new Set<Listener>();
let current: Insets = { top: 0, right: 0, bottom: 0, left: 0 };
let probe: HTMLDivElement | null = null;
let started = false;

export function getInsets(): Insets {
  return current;
}

export function onInsetsChange(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

interface TgInset {
  top?: number;
  right?: number;
  bottom?: number;
  left?: number;
}
interface TgWebApp {
  safeAreaInset?: TgInset;
  contentSafeAreaInset?: TgInset;
  onEvent?(event: string, handler: () => void): void;
}

function tg(): TgWebApp | null {
  return (window as unknown as { Telegram?: { WebApp?: TgWebApp } }).Telegram?.WebApp ?? null;
}

function readTelegram(): Insets | null {
  const wa = tg();
  if (!wa) return null;
  const sa = wa.safeAreaInset ?? {};
  const csa = wa.contentSafeAreaInset ?? {};
  return {
    top: (sa.top ?? 0) + (csa.top ?? 0),
    right: (sa.right ?? 0) + (csa.right ?? 0),
    bottom: (sa.bottom ?? 0) + (csa.bottom ?? 0),
    left: (sa.left ?? 0) + (csa.left ?? 0),
  };
}

function readCssEnv(): Insets {
  if (!probe) {
    probe = document.createElement('div');
    probe.style.cssText =
      'position:fixed;top:0;left:0;width:0;height:0;visibility:hidden;pointer-events:none;' +
      'padding-top:env(safe-area-inset-top);padding-right:env(safe-area-inset-right);' +
      'padding-bottom:env(safe-area-inset-bottom);padding-left:env(safe-area-inset-left);';
    document.body.appendChild(probe);
  }
  const cs = getComputedStyle(probe);
  return {
    top: parseFloat(cs.paddingTop) || 0,
    right: parseFloat(cs.paddingRight) || 0,
    bottom: parseFloat(cs.paddingBottom) || 0,
    left: parseFloat(cs.paddingLeft) || 0,
  };
}

function compute(): Insets {
  const css = readCssEnv();
  const t = readTelegram();
  if (!t) return css;
  return {
    top: Math.max(css.top, t.top),
    right: Math.max(css.right, t.right),
    bottom: Math.max(css.bottom, t.bottom),
    left: Math.max(css.left, t.left),
  };
}

export function refreshInsets(): void {
  const next = compute();
  if (
    next.top === current.top &&
    next.right === current.right &&
    next.bottom === current.bottom &&
    next.left === current.left
  ) {
    return;
  }
  current = next;
  for (const l of listeners) l(current);
}

/** Read once and subscribe to every source of change. Safe to call twice. */
export function initSafeArea(): void {
  refreshInsets();
  if (started) return;
  started = true;

  window.addEventListener('resize', refreshInsets);
  window.addEventListener('orientationchange', refreshInsets);

  const wa = tg();
  if (wa?.onEvent) {
    for (const ev of ['safeAreaChanged', 'contentSafeAreaChanged', 'fullscreenChanged', 'viewportChanged']) {
      try {
        wa.onEvent(ev, refreshInsets);
      } catch {
        /* event unsupported on this client */
      }
    }
  }
}
